import api from "./api";

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  branchId?: string;
  salesPointId: string;
  isActive: boolean;
  phone: string;
  /** Decrypted MPIN — only when admin resets / creates (shown once) */
  mpin?: string | null;
  /** Plain password — only when admin resets (shown once) */
  temporaryPassword?: string | null;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  branch?: { id: string; name: string; address?: string; phone?: string; email?: string };
  salesPoint?: { id: string; name: string; description?: string };
  featurePermissions?: Record<string, boolean>;
  effectiveFeaturePermissions?: string[];
  rolePackId?: string | null;
}

export interface BulkCreateUsersData {
  branchId: string;
  salesPointId: string;
  count: number;
  role: "OPERATOR" | "MERCHANT" | "GROCERY_STORE";
  emailDomain: string;
  startIndex?: number;
}

export interface BulkCreateUsersResult {
  created: User[];
  skipped: { email: string; reason: string }[];
  summary: { requested: number; created: number; skipped: number };
}

// Backend roles: SUPER_ADMIN | ADMIN | SALES_POINT_ADMIN | OPERATOR | MERCHANT | GROCERY_STORE

export interface CreateUserData {
  name: string;
  email: string;
  /** Required for ADMIN / SUPER_ADMIN; omitted for OPERATOR / MERCHANT (auto 6-digit MPIN). */
  password?: string;
  /** Optional 6-digit MPIN for OPERATOR / MERCHANT / GROCERY; blank → auto-generated. */
  mpin?: string;
  phone?: string;
  role: string;
  salesPointId?: string | null;
  branchId?: string;
  featurePermissions?: Record<string, boolean>;
  rolePackId?: string;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  phone?: string;
  role?: string;
  salesPointId?: string | null;
  branchId?: string;
  isActive?: boolean;
  password?: string;
  mpin?: string;
  /** Generate a new random 6-digit MPIN for OPERATOR / MERCHANT / GROCERY. */
  resetMpin?: boolean;
  featurePermissions?: Record<string, boolean>;
}

export type UserSortField =
  | "name"
  | "email"
  | "phone"
  | "role"
  | "branch"
  | "salesPoint"
  | "status"
  | "createdAt";

export type UserSortOrder = "ASC" | "DESC";

export interface RolePack {
  id: string;
  label: string;
  description: string;
  role: string;
  workstationTypes: string[] | "any";
  featurePermissions?: Record<string, boolean>;
}

export interface RolePacksResponse {
  packs: RolePack[];
  suggestedPackId: string | null;
}

export interface UserFilter {
  pageNo?: number;
  pageSize?: number;
  role?: string;
  salesPointId?: string;
  branchId?: string;
  isActive?: boolean;
  /** Server-side search on name, email, phone */
  search?: string;
  sortBy?: UserSortField;
  sortOrder?: UserSortOrder;
}

export const userAPI = {
  getUsers: async (filters?: UserFilter): Promise<{
    users: User[];
    total: number;
    page: number;
    totalPages: number;
  }> => {
    try {
      const params = new URLSearchParams();

      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            params.append(key, String(value));
          }
        });
      }

      const response = await api.get(`/users?${params.toString()}`);
      const responseData = response.data?.data;

      // Use the new pagination structure
      const users = (responseData?.items || []) as User[];
      const total = responseData?.totalRecords ?? responseData?.total ?? 0;
      const pageNo = responseData?.pageNo || filters?.pageNo || 1;
      const pageSize = responseData?.pageSize || filters?.pageSize || 10;

      return {
        users,
        total: total,
        page: pageNo,
        totalPages: Math.ceil(total / pageSize),
      };
    } catch (error) {
      console.error("Error fetching users:", error);
      // Return empty result on error to prevent UI crash
      return {
        users: [],
        total: 0,
        page: 1,
        totalPages: 0,
      };
    }
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data.data;
  },

  getRolePacks: async (workstationType?: string): Promise<RolePacksResponse> => {
    const params = workstationType
      ? `?workstationType=${encodeURIComponent(workstationType)}`
      : "";
    const response = await api.get(`/users/role-packs${params}`);
    return response.data.data;
  },

  createUser: async (data: CreateUserData): Promise<User> => {
    const response = await api.post("/users", data);
    return response.data.data;
  },

  bulkCreateUsers: async (data: BulkCreateUsersData): Promise<BulkCreateUsersResult> => {
    const response = await api.post("/users/bulk", data);
    return response.data.data;
  },

  updateUser: async (id: string, data: UpdateUserData): Promise<User> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data.data;
  },

  deleteUser: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};
